import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import GenericSelect from '../../../components/ui/GenericSelect';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils/Utils';
import { formatNumber, usePanel } from '../../performance-operations/config';
import { Badge } from '../../performance-operations/styles';
import { FilterRow, FilterField, ListWrap } from '../styles';
import { IDP_CATEGORIES, IDP_STATUSES, fetchPlans } from '../config';

// Analiz E12 alt alan — plan listesi: çalışan, amaç, başlangıç, bitiş, sorumlu, tamamlanma, kanıt, kontrol görüşmesi ve kapanış
// sonucu. Süzgeçler (durum, gelişim alanı, geciken aksiyon, plan gerekli) liste üstünde; grafik/KPI tıklaması bu süzgeçleri
// ayarlar (kontrollü). Satır → plan detayı (mevcut Gelişim Planı Yönetimi sayfası, ?planId= ile açılır). Başlık yalnız DataTable'da.
export default function PlanListTable({ filters, planFilters, onPlanFilters, refreshKey }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const [paging, setPaging] = useState({ pageNumber: 1, pageSize: 10 });
  const key = JSON.stringify({ filters, planFilters });
  const [lastKey, setLastKey] = useState(key);
  if (lastKey !== key) { setLastKey(key); setPaging((p) => ({ ...p, pageNumber: 1 })); }

  const list = usePanel(() => fetchPlans(filters, planFilters, paging.pageNumber, paging.pageSize),
    [key, paging.pageNumber, paging.pageSize, refreshKey], !!filters.periodId);
  const rows = useMemo(() => list.data?.rows || [], [list.data]);

  const columns = useMemo(() => [
    { name: 'employeeName', label: t('performance.developmentAnalytics.col.employee'), options: { filter: false, sort: false } },
    { name: 'title', label: t('performance.developmentAnalytics.col.goal'), options: { filter: false, sort: false } },
    {
      name: 'categories', label: t('performance.developmentAnalytics.col.category'),
      options: { filter: false, sort: false, customBodyRender: (v) => ((v || []).length ? v.map((c) => t(`performance.idp.category.${c}`, { defaultValue: c })).join(', ') : '-') },
    },
    { name: 'startDate', label: t('performance.developmentAnalytics.col.start'), options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) } },
    { name: 'endDate', label: t('performance.developmentAnalytics.col.end'), options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) } },
    { name: 'ownerName', label: t('performance.developmentAnalytics.col.owner'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    {
      name: 'completion', label: t('performance.developmentAnalytics.col.completion'),
      options: {
        filter: false, sort: false,
        customBodyRenderLite: (i) => {
          const r = rows[i];
          if (!r) return '-';
          return `${formatNumber(r.completion, lang)}% (${r.completedActions}/${r.actionCount})`;
        },
      },
    },
    {
      name: 'overdueActions', label: t('performance.developmentAnalytics.col.overdue'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? <Badge $tone="danger">{v}</Badge> : '0') },
    },
    { name: 'evidenceCount', label: t('performance.developmentAnalytics.col.evidence'), options: { filter: false, sort: false } },
    { name: 'meetingHeldAt', label: t('performance.developmentAnalytics.col.checkIn'), options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) } },
    {
      name: 'status', label: t('performance.developmentAnalytics.col.status'),
      options: { filter: false, sort: false, customBodyRender: (v) => t(`performance.idp.status.${v}`, { defaultValue: v }) },
    },
    {
      name: 'required', label: t('performance.developmentAnalytics.col.required'),
      options: { filter: false, sort: false, display: false, customBodyRender: (v) => (v ? t('performance.developmentAnalytics.yes') : t('performance.developmentAnalytics.no')) },
    },
    {
      name: 'planId', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false, viewColumns: false,
        customBodyRenderLite: (i) => {
          const r = rows[i];
          if (!r) return null;
          return (
            <OptionsMenu
              row={r}
              actions={[{ type: 'detail', label: t('performance.developmentAnalytics.action.openPlan'), onClick: () => history.push(`/talent/development-plans?planId=${r.planId}`) }]}
            />
          );
        },
      },
    },
  ], [t, lang, rows, history]);

  const onTableChanged = useCallback((action, state) => {
    if (action === 'changePage' || action === 'changeRowsPerPage') setPaging({ pageNumber: (state.page || 0) + 1, pageSize: state.rowsPerPage || 10 });
  }, []);

  const statusOptions = IDP_STATUSES.map((s) => ({ id: s, name: t(`performance.idp.status.${s}`, { defaultValue: s }) }));
  const categoryOptions = IDP_CATEGORIES.map((c) => ({ id: c, name: t(`performance.idp.category.${c}`, { defaultValue: c }) }));
  const set = (patch) => onPlanFilters({ ...planFilters, ...patch });

  return (
    <ListWrap data-panel="plans">
      <FilterRow>
        <FilterField>
          <GenericSelect
            label={t('performance.developmentAnalytics.filter.status')}
            value={planFilters.statuses?.[0] || null}
            onChange={(v) => set({ statuses: v ? [v] : [] })}
            options={statusOptions}
          />
        </FilterField>
        <FilterField>
          <GenericSelect
            label={t('performance.developmentAnalytics.filter.category')}
            value={planFilters.categories?.[0] || null}
            onChange={(v) => set({ categories: v ? [v] : [] })}
            options={categoryOptions}
          />
        </FilterField>
        <CheckboxInput label={t('performance.developmentAnalytics.filter.overdue')} checked={!!planFilters.overdue} onChange={(v) => set({ overdue: v })} />
        <CheckboxInput label={t('performance.developmentAnalytics.filter.required')} checked={!!planFilters.required} onChange={(v) => set({ required: v })} />
      </FilterRow>
      <DataTable
        title={t('performance.developmentAnalytics.list.plans')}
        fetching={list.loading}
        columns={columns}
        data={list.error ? [] : rows}
        totalRowCount={list.data?.total || 0}
        pageIndex={paging.pageNumber - 1}
        pageSize={paging.pageSize}
        onTableChanged={onTableChanged}
        disableFilters
        tableBodyMaxHeight="none"
      />
      {list.error ? <Button variant="outline" onClick={list.retry} data-action="retry-plans">{t('performance.operations.panel.retry')}</Button> : null}
    </ListWrap>
  );
}
